import { noop } from '@vueuse/core';
import {
    ClampToEdgeWrapping,
    EquirectangularReflectionMapping,
    PMREMGenerator,
    RepeatWrapping,
    SRGBColorSpace,
} from 'three';
import { defineComponent, inject, onMounted, provide } from 'vue';

import { LoaderEvent } from '@resn/gozer-loading';
import { requestIdleCallback } from '@resn/gozer-misc';
import { useRenderer } from '@resn/gozer-vue';

import { useLoaderComplete } from '~/composables/useLoaderComplete';

export const GlobalAssetsKey = Symbol('GlobalAssets');

export const useGlobalAssets = (onLoaded = noop) => {
    const provider = inject(GlobalAssetsKey);

    if (!provider) {
        throw new Error('GlobalAssets provider not found');
    }

    onMounted(() => {
        if (provider.loader.state === 'complete') onLoaded({ data: provider.loader.data });
        provider.loader.once(LoaderEvent.LOAD_COMPLETE, onLoaded);
    });

    return provider;
};

export const GlobalAssets = defineComponent({
    props: {
        assets: {
            type: Object,
            default: () => ({}),
        },
    },

    async setup(props) {
        const { renderer } = useRenderer();

        const onLoaded = ({ data }) => {
            provider.assets = data;

            Object.entries(data).forEach(([key, asset]) => {
                if (!asset?.isTexture) return;

                asset.colorSpace = SRGBColorSpace;
                // Matcaps are spherical lit-sphere lookups; tiling breaks sampling and reads as flat grey.
                const isMatcapTexture = /^matcap/i.test(key);
                asset.wrapS = asset.wrapT = isMatcapTexture ? ClampToEdgeWrapping : RepeatWrapping;

                asset.flipY = false;
                asset.needsUpdate = true;
            });

            // Pre-process the envMap through PMREMGenerator so that Three.js
            // doesn't run the expensive _textureToCubeUV conversion at render time
            if (data.envMap) {
                data.envMap.mapping = EquirectangularReflectionMapping;

                const pmremGenerator = new PMREMGenerator(renderer);
                const pmremRT = pmremGenerator.fromEquirectangular(data.envMap);

                data.envMapPMREM = pmremRT.texture;

                pmremGenerator.dispose();
            }

            // Pre-upload non-compressed textures to the GPU so that texSubImage2D
            // doesn't cause jank when materials first render (e.g. card reveal).
            // KTX2/compressed textures don't need this — they're already GPU-ready.
            requestIdleCallback(() => {
                Object.values(data).forEach((asset) => {
                    if (asset?.isTexture && !asset.isCompressedTexture) {
                        renderer.initTexture(asset);
                    }
                });

                if (data.envMapPMREM) renderer.initTexture(data.envMapPMREM);
            });
        };

        const loader = useLoaderComplete(
            {
                'card-psa': '/glb/slab-psa-etc1s@lg.glb',
                'card-cgc': '/glb/slab-cgc-etc1s@lg.glb',
                'card-bgs': '/glb/slab-bgs-etc1s@lg.glb',
                'card-sgc': '/glb/slab-sgc-etc1s@lg.glb',

                envMap: '/textures/env-map/env-map-6@lg.webp#texture',

                matcapDiffuse: '/textures/matcap/slab-diffuse-etc1s.ktx2',

                // Card textures
                'card-mask-psa': '/textures/card/card-mask-psa-etc1s.ktx2',
                'card-mask-cgc': '/textures/card/card-mask-cgc-etc1s.ktx2',
                'card-mask-bgs': '/textures/card/card-mask-bgs-etc1s.ktx2',
                'card-mask-sgc': '/textures/card/card-mask-sgc-etc1s.ktx2',

                noise1: '/textures/noise/noise.png#texture',
                matcapIrridescent: '/textures/matcap/matcap-irridescent-4@lg.webp#texture',
                matcapIrridescent3: '/textures/matcap/matcap-irridescent-3-etc1s.ktx2',

                // pNoise: '/textures/noise/pnoise1-etc1s.ktx2',
                pNoise: '/textures/noise/pnoise1@lg.webp#texture',
                pNoiseNormal: '/textures/noise/noise-normal@lg.webp#texture',
                pNoiseNormalGold: '/textures/noise/noise-normal-foil-gold@lg.webp#texture',

                // 'pack-super': '/textures/pack/test/super@lg.webp#texture',
                // 'pack-mega': '/textures/pack/test/mega@lg.webp#texture',
                // 'pack-ultra': '/textures/pack/test/ultra@lg.webp#texture',
                // 'pack-infinity': '/textures/pack/test/infinity@lg.webp#texture',

                // matcapPackGlobal: '/textures/pack/pack-neutral-1024-etc1s.ktx2',
                // matcapPackGlobal: '/textures/pack/pack-neutral-1024@lg.webp#texture',
                // matcapPackGlobal: '/textures/pack/pack-gold-2-512.png#texture', // gold test
                // matcapPackGlobal: '/textures/pack/pack-global-512-etc1s.ktx2',
                matcapPackGlobal: '/textures/pack/pack-global-512-etc1s.ktx2',
                // matcapPackGold: '/textures/pack/pack-gold-512.png#texture',
                matcapPackGold: '/textures/pack/pack-gold-512-etc1s.ktx2',
                matcapPackBlack: '/textures/pack/pack-black-512-etc1s.ktx2',
                matcapPackWhite: '/textures/matcap/white-matte-etc1s.ktx2',
                matcapPackGrey: '/textures/matcap/grey-matte-etc1s.ktx2',
                matcapSpecialPack: '/textures/matcap/matcap-special-pack-etc1s.ktx2',
                matcapPackRough: '/textures/pack/pack-rough-512-etc1s.ktx2',

                packSceneObjects: '/glb/pack/pack-scene-objects-etc1s@sm.glb',

                ...props.assets,
            },
            onLoaded
        );

        const provider = {
            assets: null,
            loader,
        };

        provide(GlobalAssetsKey, provider);
    },

    render() {
        if (this.$slots.default) {
            return this.$slots.default();
        }
        return null;
    },
});
